import { useEffect, useState } from 'react';
import { Modal, TextInput } from '@carbon/react';
import style from './modals.module.scss';

interface RenameWorkspaceModalProps {
  isOpen: boolean;
  isLoading: boolean;
  onClose: () => void;
  onSubmit: (newName: string) => void;
  currentName: string;
}

/**
 * Modal shown when the user clicks "Rename Workspace". Pre-fills the current
 * name and disables the primary button until the name differs.
 *
 * @param {RenameWorkspaceModalProps} props - Component props.
 * @return {JSX.Element} The rename workspace modal.
 */
export const RenameWorkspaceModal = ({
  isOpen,
  isLoading,
  onClose,
  onSubmit,
  currentName,
}: RenameWorkspaceModalProps): JSX.Element => {
  const [name, setName] = useState(currentName);

  useEffect(() => {
    if (isOpen) setName(currentName);
  }, [isOpen, currentName]);

  /**
   * Handles form submission by delegating the trimmed name to the parent.
   */
  const handleSubmit = () => {
    onSubmit(name.trim());
  };

  const trimmed = name.trim();
  const isSubmitDisabled =
    isLoading || trimmed === '' || trimmed === currentName;

  return (
    <Modal
      open={isOpen}
      modalHeading="Rename Workspace"
      primaryButtonText={isLoading ? 'Renaming...' : 'Rename'}
      secondaryButtonText="Cancel"
      primaryButtonDisabled={isSubmitDisabled}
      onRequestSubmit={handleSubmit}
      onRequestClose={isLoading ? undefined : onClose}
      className={style.renameModal}
    >
      <TextInput
        id="rename-workspace-name"
        labelText="Workspace name"
        value={name}
        disabled={isLoading}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setName(e.target.value)
        }
      />
    </Modal>
  );
};
